import { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from '../context/DataContext';
import ApiService from '../../common/ApiCalls';
import { checkQuickViewPermission } from '../util/permissionCheck';
import { searchTimeOut } from '../util/miscellaneousService';
import { SEARCH_OPTIONS } from '../../consts/g-map-search';
import { DEFAULT_MAP_COORDINATES } from '../../consts/g-map-options';
import { MAP_EVENTS } from '../../consts/map-events';

const getSearchSettings = () => {
  try {
    return JSON.parse(window.localStorage.getItem('lastSearchDetails'));
  } catch {
    return null;
  }
};

const getRangeOption = () => {
  try {
    return !!JSON.parse(window.localStorage.getItem('rangeOption'));
  } catch {
    return false;
  }
};

const parseMapBounds = (bounds) => {
  try {
    const parsedBounds = typeof bounds === 'string' ? JSON.parse(bounds) : bounds;
    return parsedBounds?.ne && parsedBounds?.sw ? parsedBounds : null;
  } catch {
    return null;
  }
};

const setSearchSettings = ({ text, option, zoneId, executiveGroupId }) => {
  const initialSearchSettings = { text: '', option: '' };
  const localStorageSearchSettings = getSearchSettings() || { ...initialSearchSettings };

  window.localStorage.setItem(
    'lastSearchDetails',
    JSON.stringify({
      ...localStorageSearchSettings,
      ...((!!text || text === '') && { text }),
      ...((!!option || option === '') && { option }),
      ...(zoneId !== undefined && { zoneId }),
      ...(executiveGroupId !== undefined && { executiveGroupId })
    })
  );
};

const ALL_PINS_SEARCH_TERMS = new Set(['all', 'world']);

export const isAllPinsSearchTerm = (searchText) => (
  typeof searchText === 'string' &&
  ALL_PINS_SEARCH_TERMS.has(searchText.trim().toLowerCase())
);

export const useMapSearch = () => {
  const {
    locdata,
    selectedText,
    id,
    type,
    showViewTabs,
    imageSliderState,
    showBounds,
    markersData,
    advancedSearchData,
    collapseIsOpenData
  } = useContext(GlobalContext);
  const [initialSearchSettings] = useState(() => getSearchSettings() || {});
  const lastAutomaticSearchRef = useRef(null);
  const searchRequestRef = useRef(0);
  const executiveRequestRef = useRef(0);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showQuickView, setShowQuickView] = useState(false);
  const [quickViewData, setQuickViewData] = useState({});
  const [searchText, setSearchText] = useState(
    () => initialSearchSettings.text || ''
  );
  const [showAll, setShowAll] = useState(false);
  const [showRes, setShowRes] = useState(
    () => Boolean(initialSearchSettings.text)
  );
  const [allowed, setAllowed] = useState(true);
  const [searchOption, setSearchOption] = useState(
    () => initialSearchSettings.option || SEARCH_OPTIONS.DEFAULT
  );
  const [zoneId, setZoneId] = useState(null);
  const [executiveGroupId, setExecutiveGroupId] = useState(null);
  const [executiveEntity, setExecutiveEntity] = useState(null);
  const [data, setData] = locdata;
  const [locId, setLocId] = id;
  const { 1: setSelectedLoc } = selectedText;
  const { 1: setShowTabs } = showViewTabs;
  const { 1: setShowImages } = imageSliderState;
  const [bounds] = showBounds;
  const [markers] = markersData;
  const [advancedSearch] = advancedSearchData;
  const [collapseIsOpen, setCollapseIsOpen] = collapseIsOpenData;

  useEffect(() => {
    // Detail pages use selectedLoc as their heading. Do not let that transient
    // selection filter a persisted map search when the user returns.
    setSelectedLoc('');
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const advancedSearchCall = async () => {
      let res;
      if (searchText.length) {
        res = await ApiService().advancedSearch(
          searchText,
          searchOption,
          advancedSearch.category1,
          advancedSearch.category2,
          advancedSearch.filterType
        );
      } else {
        res = await ApiService().advancedSearchEmpty(
          searchOption,
          advancedSearch.category1,
          advancedSearch.category2,
          advancedSearch.filterType
        );
      }
      console.log('Advanced Search Results: ', res);
    };
    if (Object.keys(advancedSearch).length !== 0) {
      setSearchLoading(true);
      advancedSearchCall().finally(() => setSearchLoading(false));
    }
    // eslint-disable-next-line
  }, [advancedSearch]);

  const [sortAsc, setSortAsc] = useState(true);
  const toggleSort = () => setSortAsc((prevState) => !prevState);

  const [sortedSearchSuggestions, setSortedSearchSuggestions] = useState([]);

  const toggleCollapseIsOpen = () => setCollapseIsOpen((prevState) => !prevState);

  const handleSearch = (e) => {
    const searchInput = e.target.value;
    setCollapseIsOpen(true);
    setSearchSettings({ text: searchInput });
    setSearchText(searchInput);
    setSelectedLoc('');
    setShowRes(true);
    setShowAll(false);
    setShowQuickView(false);
    setShowTabs(false);
    setShowImages(false);
    setLocId('');
  };

  const resetSearchState = () => {
    setData([]);
    setSortAsc(true);
    setSortedSearchSuggestions([]);
    setSelectedLoc('');
    setShowAll(false);
    setShowQuickView(false);
    setShowTabs(false);
    setShowImages(false);
  };

  const clearSearch = () => {
    setSearchSettings({ text: '' });
    window.localStorage.removeItem('searchedString');
    resetSearchState();
    setSearchText('');
    setShowRes(false);
    setLocId('');
  };

  const showAllResults = () => {
    searchLocation();
    setShowAll(true);
  };

  const emitMapZone = (zone) => {
    try {
      window.dispatchEvent(new CustomEvent(MAP_EVENTS.ZONE, { detail: zone || null }));
    } catch (e) { console.log('Window not available', e) }
  };

  const emitMapExecutive = (payload) => {
    try {
      window.dispatchEvent(new CustomEvent(MAP_EVENTS.EXECUTIVE, { detail: payload || null }));
    } catch (e) { console.log('Window not available', e) }
  };

  const onZonePicked = async (zone) => {
    const selectedZoneId = zone?.id || null;
    setZoneId(selectedZoneId);
    setSearchSettings({ zoneId: selectedZoneId });
    if (!zone) {
      setData([]);
      emitMapZone(null);
      return;
    }
    setShowRes(false);
    setShowQuickView(false);
    setSearchLoading(true);
    try {
      const res = await ApiService().searchZone({ zoneId: zone.id });
      const payload = res?.data || res || {};
      const results = filterEntitySearchAssets(payload.data || []);
      setData(results || []);
      const zones = Array.isArray(payload.zones) && payload.zones.length
        ? payload.zones
        : payload.zone
          ? [payload.zone]
          : [];
      emitMapZone(zones.length ? zones : null);
    } catch (err) {
      console.error('Zone search failed', err);
      setData([]);
      emitMapZone(null);
    } finally {
      setSearchLoading(false);
    }
  };

  const onExecutivePicked = async (groupId) => {
    const requestId = ++executiveRequestRef.current;
    searchRequestRef.current += 1;
    setExecutiveGroupId(groupId || null);
    setSearchSettings({ executiveGroupId: groupId || null });
    setExecutiveEntity(null);
    if (!groupId) {
      setData([]);
      emitMapExecutive(null);
      return;
    }
    setShowRes(false);
    setShowQuickView(false);
    setSearchLoading(true);
    try {
      const res = await ApiService().searchExecutive({ groupId });

      const body = res?.data ?? res;

      const adapt = (r) => {
        if (r?.type === 'FeatureCollection' && Array.isArray(r.features)) {
          const pins = {
            type: 'FeatureCollection',
            features: r.features.filter(f => f?.geometry?.type === 'Point')
          };
          const zones = {
            type: 'FeatureCollection',
            features: r.features.filter(f => ['Polygon','MultiPolygon'].includes(f?.geometry?.type))
          };
          return { zones, pins, entities: [] };
        }
        if (r?.zones || r?.pins || r?.entities) {
          return {
            zones: r.zones ?? null,
            pins: r.pins ?? null,
            entities: Array.isArray(r.entities) ? r.entities : []
          };
        }
        if (Array.isArray(r)) return { zones: r, pins: null, entities: [] };
        return { zones: null, pins: null, entities: [] };
      };

      const { zones, pins, entities } = adapt(body);
      if (requestId !== executiveRequestRef.current) return;

      setData([]);
      emitMapExecutive({ zones, pins, entities });
    } catch (err) {
      if (requestId !== executiveRequestRef.current) return;
      console.error('Executive search failed', err);
      setData([]);
      emitMapExecutive(null);
    } finally {
      if (requestId === executiveRequestRef.current) setSearchLoading(false);
    }
  };

  const getBuilding = async (place) => {
    const { id, name, type } = place.value[0];
    setLocId(id);
    window.localStorage.setItem('locId', id);
    window.localStorage.setItem('locIdForMap', id);
    window.localStorage.setItem('searchTab', searchText);
    setSearchSettings({ text: name === null ? name : name });
    setSelectedLoc(name === null ? name : name);

    if (place.value.length === 1) {
      if (id) openQuickView(id, type);
      setShowRes(false);
    } else {
      setTimeout(() => {
        const targetId = place?.value?.[0]?.id;
        if (!targetId) return;
        const m = markers.find((mk) => mk?.assetId === targetId);
        if (m) window.google.maps.event.trigger(m, 'mouseover');
      }, 60);
    }
  };

  const getQuickViewData = async (id) => {
    let quickViewDataResp = await ApiService().getAsset(id);
    if (quickViewDataResp) {
      const { account_id } = quickViewDataResp.id;
      let permissions = quickViewDataResp['__permissions__'];
      window.localStorage.setItem('accountId', account_id);
      quickViewDataResp = await reconstructedQuickViewData(quickViewDataResp, type);
      checkQuickViewPermission(setAllowed, permissions);
      setQuickViewData(quickViewDataResp);
    }
  };

  const reconstructedQuickViewData = async (quickViewData, type) => {
    let address;
    let cord;
    const owner = { email: '', profile: { title: '' }, first_name: '', last_name: '', phone: '' };
    try {
      address = `${quickViewData.address.unit || ''} ${quickViewData.address.street} ${quickViewData.address.city} ${quickViewData.address.region} ${quickViewData.address.postal}`;
    } catch {
      address = '';
    }
    if (quickViewData.geo !== null) {
      cord = `${quickViewData.geo.lat},${quickViewData.geo.long}`;
    } else {
      cord = `${quickViewData.address.lat},${quickViewData.address.long}`;
    }
    return {
      account_id: quickViewData.id,
      address: address,
      address_coordinates: cord,
      bec_building_id: null,
      building_id: quickViewData.id,
      building_name: quickViewData.name,
      building_reference: null,
      contact_email_address: owner.email || '',
      contact_job_title: owner.profile.title || 'Not Set',
      contact_name: `${owner.first_name || ''} ${owner.last_name || ''}`,
      contact_phone_number: owner.phone || '',
      customer_name: null,
      images: null,
      name: quickViewData.name,
      location_id: quickViewData.id,
      location_job_id: quickViewData.location_job_id || '',
      location_job_number: null,
      location_phone_number: quickViewData.phone,
      transit_id: quickViewData.id
    };
  };

  const openQuickView = async (id) => {
    setSearchLoading(true);
    await getQuickViewData(id);
    setShowQuickView(true);
    setShowRes(false);
    setLocId(id);
    setSearchLoading(false);
  };

  const searchLocation = async () => {
    const mapBounds = parseMapBounds(bounds);

    if (!mapBounds) return;
    const requestId = ++searchRequestRef.current;
    const showAllPins = isAllPinsSearchTerm(searchText);

    try {
      setSearchLoading(true);

      const props = {
        queryString: showAllPins ? undefined : `${searchText}`,
        mapBounds,
        range: showAllPins ? false : getRangeOption()
      };
      const locationArray = await ApiService().searchLocation(props);
      if (requestId !== searchRequestRef.current) return;
      if (locationArray?.data) {
        const data = filterEntitySearchAssets(locationArray.data.data) || [];
        const unfilteredData = locationArray.data.unfilteredData || [];
        if (data && unfilteredData) {
          if (data.length === 0) setData(unfilteredData);
          else setData(data);
        }
      }
    } catch (err) {
      if (requestId !== searchRequestRef.current) return;
      console.error('Failed to perform search', err);
      resetSearchState();
    } finally {
      if (requestId === searchRequestRef.current) setSearchLoading(false);
    }
  };

  const filterEntitySearchAssets = (searchArray) => {
    const assetsArray = [];
    let hasEntities = false;

    searchArray.forEach((item) => {
      if (item.type === 'asset') {
        assetsArray.push(item);
      } else if (item.type === 'entity') {
        hasEntities = true;
        if (Array.isArray(item?.assets)) assetsArray.push(...item.assets);
      }
    });

    const uniqueAssets = new Map();
    assetsArray.forEach((asset) => {
      if (!uniqueAssets.has(asset.id)) uniqueAssets.set(asset.id, asset);
    });

    if (hasEntities) searchArray = Array.from(uniqueAssets.values());
    return searchArray;
  };

  const calculateDistance = (userCoords, locationCoords) => {
    const EARTH_RADIUS = 3958.8;
    const rlat1 = userCoords.lat * (Math.PI / 180);
    const rlat2 = locationCoords.lat * (Math.PI / 180);
    const latDiff = rlat2 - rlat1;
    const longDiff = (userCoords.long - locationCoords.long) * (Math.PI / 180);

    const distance =
      2 *
      EARTH_RADIUS *
      Math.asin(
        Math.sqrt(
          Math.sin(latDiff / 2) * Math.sin(latDiff / 2) +
            Math.cos(rlat1) *
              Math.cos(rlat2) *
              Math.sin(longDiff / 2) *
              Math.sin(longDiff / 2)
        )
      );
    return distance;
  };

  const attachBrutalForceCalculatedDistance = (suggestionArray) => {
    const setDistanceForUserLocation = (position) => {
      const whereAmI = { lat: position.coords.latitude, long: position.coords.longitude };
      suggestionArray.forEach((oneSuggestion) => {
        const rawLocation = oneSuggestion?.value[0];
        if (rawLocation.lat && rawLocation.long) {
          oneSuggestion.distance = calculateDistance(whereAmI, rawLocation);
        } else {
          console.warn(`Location #${rawLocation.id} has incorrect lat-long information`);
          oneSuggestion.distance = Number.MAX_VALUE;
        }
      });
    };

    const setDistanceForDefaultLocation = () => {
      const whereAmI = { lat: DEFAULT_MAP_COORDINATES.LAT, long: DEFAULT_MAP_COORDINATES.LONG };
      suggestionArray.forEach((oneSuggestion) => {
        const rawLocation = oneSuggestion?.value[0];
        if (rawLocation.lat && rawLocation.long) {
          oneSuggestion.distance = calculateDistance(whereAmI, rawLocation);
        } else {
          console.warn(`Location #${rawLocation.id} has incorrect lat-long information`);
          oneSuggestion.distance = Number.MAX_VALUE;
        }
      });
    };

    navigator.geolocation.getCurrentPosition(
      setDistanceForUserLocation,
      setDistanceForDefaultLocation
    );
  };

  let searchSuggestions = [];
  if (Array.isArray(data) && data.length) {
    searchSuggestions = Array.from(
      data.reduce((acc, locationData) => {
        const coordinates = { long: Number(locationData.long), lat: Number(locationData.lat) };
        const normalizedLatitude = Number.isNaN(coordinates.lat) ? locationData.id : coordinates.lat;

        if (acc.has(normalizedLatitude)) {
          const tmpData = acc.get(normalizedLatitude);
          tmpData.push(locationData);
          acc.set(normalizedLatitude, tmpData);
        } else {
          acc.set(normalizedLatitude, [{ ...locationData }]);
        }

        return acc;
      }, new Map()).entries()
    ).map(([name, value]) => ({ name, value }));
  }

  attachBrutalForceCalculatedDistance(searchSuggestions);

  const handleKeypress = async (event) => {
    if (event.keyCode === 13) {
      showAllResults();
      setCollapseIsOpen(true);
      if (searchSuggestions.length === 1) {
        await getBuilding(searchSuggestions[0]);
      }
    }
  };

  useEffect(() => {
    const handleExecutiveEntitySelected = (event) => {
      if (!event.detail?.id) return;
      setExecutiveEntity(event.detail);
      setShowQuickView(false);
      setShowRes(false);
    };

    window.addEventListener(
      MAP_EVENTS.EXECUTIVE_ENTITY_SELECTED,
      handleExecutiveEntitySelected
    );
    return () => window.removeEventListener(
      MAP_EVENTS.EXECUTIVE_ENTITY_SELECTED,
      handleExecutiveEntitySelected
    );
  }, []);

  useEffect(() => {
    searchRequestRef.current += 1;
    if (searchOption !== SEARCH_OPTIONS.ZONE) {
      setZoneId(null);
      emitMapZone(null);
    } else {
      const persistedZoneId = getSearchSettings()?.zoneId;
      if (persistedZoneId) void onZonePicked({ id: persistedZoneId });
    }
    if (searchOption !== SEARCH_OPTIONS.EXECUTIVE) {
      setExecutiveGroupId(null);
      setExecutiveEntity(null);
      emitMapExecutive(null);
    } else {
      const persistedGroupId = getSearchSettings()?.executiveGroupId;
      if (persistedGroupId) void onExecutivePicked(persistedGroupId);
    }
    // Selection restoration is intentionally keyed to the active map mode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchOption]);

  useEffect(() => {
    if (locId) {
      openQuickView(locId);
    }
    // eslint-disable-next-line
  }, [locId]);

  useEffect(() => {
    setSearchSettings({
      text: searchText,
      option: typeof searchOption === 'string' ? searchOption : ''
    });
    if ([SEARCH_OPTIONS.ZONE, SEARCH_OPTIONS.EXECUTIVE].includes(searchOption)) return;
    if (searchText.length <= 1) {
      lastAutomaticSearchRef.current = null;
      resetSearchState();
      return;
    }

    if (!parseMapBounds(bounds)) return;

    const automaticSearchKey = `${searchOption}\u0000${searchText}`;
    if (lastAutomaticSearchRef.current === automaticSearchKey) return;

    return searchTimeOut(searchText, () => {
      lastAutomaticSearchRef.current = automaticSearchKey;
      searchLocation();
    });
    // eslint-disable-next-line
  }, [bounds, searchText, searchOption]);

  useEffect(() => {
    let timeOut;
    if (searchSuggestions.length) {
      timeOut = setTimeout(() => {
        searchSuggestions.sort((a, b) => (sortAsc ? a.distance - b.distance : b.distance - a.distance));
        setSortedSearchSuggestions(searchSuggestions);
      }, 20);
    } else {
      setSortedSearchSuggestions([]);
    }
    return () => { clearTimeout(timeOut); };
    // eslint-disable-next-line
  }, [sortAsc, data]);

  return {
    searchLoading,
    searchSuggestions,
    getBuilding,
    handleSearch,
    clearSearch,
    handleKeypress,
    showQuickView,
    setShowQuickView,
    quickViewData,
    allowed,
    showRes,
    showAll,
    showAllResults,
    searchText,
    setSearchText,
    searchOption,
    setSearchOption,
    zoneId,
    onZonePicked,
    executiveGroupId,
    onExecutivePicked,
    executiveEntity,
    setExecutiveEntity,
    toggleCollapseIsOpen,
    collapseIsOpen,
    sortAsc,
    toggleSort,
    sortedSearchSuggestions,
    setShowRes,
    getSearchSettings,
    setSearchSettings
  };
};
